import React, { useEffect, useState } from 'react'
import css from './style.scss'

const RANK_COLORS = ['#7C3AED', '#4F46E5', '#2563EB']
const OTHER_COLOR = '#64748B'

function fmt(n) {
  if (n == null || n === 0) return '—'
  return `$${Number(n).toLocaleString()}`
}

function fmtShort(n) {
  if (n == null || n === 0) return '—'
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `$${Math.round(n / 1000).toLocaleString()}K`
  return `$${n.toLocaleString()}`
}

function num(n) {
  if (n == null) return '—'
  return Number(n).toLocaleString()
}

export default function InvestorDetailModal({ investor, rank, matchScore, onClose }) {
  const [showAddresses, setShowAddresses] = useState(false)

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [onClose])

  if (!investor) return null

  const hold = investor.currentHoldings || {}
  const acq = investor.acquisitions || {}
  const disp = investor.dispositions || {}
  const contacts = investor.contacts || []
  const mailingAddresses = investor.mailingAddresses || []
  const classifications = investor.classification?.length > 0 ? investor.classification : []

  const color = rank < RANK_COLORS.length ? RANK_COLORS[rank] : OTHER_COLOR
  const cashCount = hold.cashPurchasePropertyCount || 0
  const financedCount = hold.financedPropertyCount || 0
  const totalCashAndFin = cashCount + financedCount
  const cashPct = totalCashAndFin > 0 ? Math.round((cashCount / totalCashAndFin) * 100) : 0
  const dist = hold.propertyTypeDistribution || {}

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div className={css.modalBackdrop} onClick={handleBackdropClick}>
      <div className={css.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={css.modalHeader}>
          <div className={css.modalHeaderLeft}>
            <div className={css.rankBadge} style={{ background: color }}>
              {rank === 0 ? '#1 BEST MATCH' : `#${rank + 1}`}
            </div>
            <div className={css.modalTitle}>{investor.name || 'Unknown Investor'}</div>
            <div className={css.modalSubtitle}>
              {investor.type || 'Investor'}
              {classifications.length > 0 && ` · ${classifications.join(', ')}`}
              {matchScore != null && ` · ${matchScore}% match`}
            </div>
          </div>
          <button className={css.modalCloseBtn} onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Body */}
        <div className={css.modalBody}>
          {/* Current Holdings */}
          <div className={css.modalSection}>
            <div className={css.modalSectionTitle}>Current Holdings</div>
            <div className={css.modalStatsGrid}>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Total Properties</div>
                <div className={css.modalStatValue}>{num(hold.totalPropertyCount)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Avg Purchase Price</div>
                <div className={css.modalStatValue}>{fmtShort(hold.avgPurchasePrice)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Avg Estimated Value</div>
                <div className={css.modalStatValue}>{fmtShort(hold.avgEstimatedValue)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Total Equity</div>
                <div className={css.modalStatValue}>{fmtShort(hold.totalEquity)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Total Est Portfolio Value</div>
                <div className={css.modalStatValue}>{fmtShort(hold.totalEstimatedValue)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Cash Purchases</div>
                <div className={css.modalStatValue}>{num(cashCount)} ({cashPct}%)</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Financed</div>
                <div className={css.modalStatValue}>{num(financedCount)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Avg Ownership</div>
                <div className={css.modalStatValue}>{hold.avgOwnershipLengthMonths ? `${hold.avgOwnershipLengthMonths} mo` : '—'}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Owner Occupied</div>
                <div className={css.modalStatValue}>{num(hold.ownerOccupiedPropertyCount)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Non-Owner Occupied</div>
                <div className={css.modalStatValue}>{num(hold.nonOwnerOccupiedPropertyCount)}</div>
              </div>
            </div>

            {/* Property Type Distribution */}
            {(dist.residentialSingleFamilyCount > 0 || dist.residentialMultiFamilyCount > 0) && (
              <div className={css.modalDistribution}>
                <div className={css.modalSubLabel}>Property Type Mix:</div>
                <div className={css.modalDistRow}>
                  {dist.residentialSingleFamilyCount > 0 && (
                    <span className={css.modalDistTag}>{dist.residentialSingleFamilyCount} Single Family</span>
                  )}
                  {dist.residentialMultiFamilyCount > 0 && (
                    <span className={css.modalDistTag}>{dist.residentialMultiFamilyCount} Multi-Family</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Acquisitions */}
          <div className={css.modalSection}>
            <div className={css.modalSectionTitle}>Acquisitions History</div>
            <div className={css.modalStatsGrid}>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Total (all time)</div>
                <div className={css.modalStatValue}>{num(acq.totalAcquisitionCount)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Last 12 Months</div>
                <div className={css.modalStatValue}>{num(acq.totalAcquisitionCountLast12Months)} ({fmtShort(acq.totalAcquisitionPriceLast12Months)})</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Avg Price (12mo)</div>
                <div className={css.modalStatValue}>{fmtShort(acq.avgAcquisitionPriceLast12Months)}</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Last 36 Months</div>
                <div className={css.modalStatValue}>{num(acq.totalAcquisitionCountLast36Months)} ({fmtShort(acq.totalAcquisitionPriceLast36Months)})</div>
              </div>
              <div className={css.modalStat}>
                <div className={css.modalStatLabel}>Last 60 Months</div>
                <div className={css.modalStatValue}>{num(acq.totalAcquisitionCountLast60Months)} ({fmtShort(acq.totalAcquisitionPriceLast60Months)})</div>
              </div>
            </div>
          </div>

          {/* Dispositions */}
          {(disp.totalDispositionCount > 0) && (
            <div className={css.modalSection}>
              <div className={css.modalSectionTitle}>Dispositions History</div>
              <div className={css.modalStatsGrid}>
                <div className={css.modalStat}>
                  <div className={css.modalStatLabel}>Total (all time)</div>
                  <div className={css.modalStatValue}>{num(disp.totalDispositionCount)}</div>
                </div>
                <div className={css.modalStat}>
                  <div className={css.modalStatLabel}>Last 12 Months</div>
                  <div className={css.modalStatValue}>{num(disp.totalDispositionCountLast12Months)} ({fmtShort(disp.totalDispositionPriceLast12Months)})</div>
                </div>
                <div className={css.modalStat}>
                  <div className={css.modalStatLabel}>Avg Sale Price (12mo)</div>
                  <div className={css.modalStatValue}>{fmtShort(disp.avgDispositionPriceLast12Months)}</div>
                </div>
                <div className={css.modalStat}>
                  <div className={css.modalStatLabel}>Last 36 Months</div>
                  <div className={css.modalStatValue}>{num(disp.totalDispositionCountLast36Months)} ({fmtShort(disp.totalDispositionPriceLast36Months)})</div>
                </div>
              </div>
            </div>
          )}

          {/* Contacts */}
          {contacts.length > 0 && (
            <div className={css.modalSection}>
              <div className={css.modalSectionTitle}>Contact Info</div>
              {contacts.map((contact, idx) => {
                const fullName = contact.name?.full || 'Unknown Contact'
                const phones = contact.phones || []
                const emails = contact.emails || []
                return (
                  <div key={idx} className={css.modalContactBlock}>
                    <div className={css.modalContactName}>{fullName}</div>
                    {phones.length > 0 && (
                      <div className={css.modalContactList}>
                        {phones.map((p, i) => (
                          <div key={i} className={css.modalContactItem}>
                            <span className={css.modalContactType}>{p.type || 'Phone'}</span>
                            <span>{p.number}</span>
                            {p.reachable && <span className={css.modalContactBadge}>Reachable</span>}
                            {p.dnc && <span className={css.modalContactBadgeWarn}>DNC</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {emails.length > 0 && (
                      <div className={css.modalContactList}>
                        {emails.map((e, i) => (
                          <div key={i} className={css.modalContactItem}>
                            <span className={css.modalContactType}>Email</span>
                            <span>{e.email}</span>
                            {e.tested && <span className={css.modalContactBadge}>Verified</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Mailing Addresses (collapsible) */}
          {mailingAddresses.length > 0 && (
            <div className={css.modalSection}>
              <button
                className={css.modalCollapseToggle}
                onClick={() => setShowAddresses(!showAddresses)}
              >
                <span className={css.modalSectionTitle}>
                  Mailing Addresses ({mailingAddresses.length})
                </span>
                <span className={css.modalCollapseArrow}>{showAddresses ? '▲' : '▼'}</span>
              </button>
              {showAddresses && (
                <div className={css.modalAddressList}>
                  {mailingAddresses.map((addr, i) => (
                    <div key={i} className={css.modalAddressRow}>
                      <div className={css.modalAddressMain}>
                        <div className={css.modalAddressText}>{addr.fullAddress || addr.street || '—'}</div>
                        {addr.addressValidity && (
                          <div className={css.modalAddressMeta}>
                            {addr.addressValidity === 'Valid' ? '✓ Valid' : '⚠ Unverified'}
                          </div>
                        )}
                      </div>
                      <div className={css.modalAddressCount}>
                        <strong>{addr.propertyCount || 1}</strong>
                        <span> {addr.propertyCount === 1 ? 'property' : 'properties'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
